import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "How does Rentify make money?",
    answer: "We take a small service fee from renters. Listers always keep 100% of their listed price."
  },
  {
    question: "What if someone damages my item?",
    answer: "Damage claims can be filed within 72 hours of the rental ending, with photo evidence. Rentify reviews claims and mediates between renter and lister."
  },
  {
    question: "What happens if an item is returned late?",
    answer: "There's a grace period until 11:59 PM on the day the rental ends. After that, a late fee equal to one extra day at the daily rate is charged automatically."
  },
  {
    question: "How do I get paid?",
    answer: "Fast, direct payouts after the rental is completed."
  },
  {
    question: "Is it really free to list items?",
    answer: "Yes — listing is free and you keep 100% of your listed rental price."
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1] as const
    }
  }
};

const FAQ = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  return (
    <section ref={sectionRef} className="relative py-24 overflow-hidden bg-background">
      {/* Background orbs */}
      <div className="absolute top-20 right-1/4 w-64 h-64 rounded-full bg-primary/5 blur-3xl floating-orb" />
      <div className="absolute bottom-20 left-1/4 w-72 h-72 rounded-full bg-accent/5 blur-3xl floating-orb stagger-2" />
      
      <div className="container px-4 sm:px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Frequently Asked Questions
          </h2>
        </motion.div>
        
        <motion.div 
          className="max-w-3xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-50px" }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="relative group">
                  {/* Glow effect on hover */}
                  <motion.div 
                    className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 blur-sm"
                    style={{
                      background: "linear-gradient(135deg, hsl(var(--primary) / 0.2), hsl(var(--accent) / 0.15), hsl(var(--primary) / 0.2))"
                    }}
                  />
                  
                  <AccordionItem 
                    value={`item-${index}`}
                    className="relative bg-card border border-border/40 rounded-2xl px-6 shadow-soft transition-all duration-300 group-hover:shadow-3d group-hover:border-primary/20 overflow-hidden"
                  >
                    {/* Spotlight effect */}
                    {hoveredIndex === index && (
                      <motion.div
                        className="absolute inset-0 pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                          background: "radial-gradient(circle at 50% 0%, hsl(var(--primary) / 0.08), transparent 50%)"
                        }}
                      />
                    )}
                    
                    <AccordionTrigger className="relative text-left font-semibold text-foreground hover:no-underline py-5 group-hover:text-primary transition-colors duration-300 [&>svg]:hidden">
                      <span className="flex-1">{faq.question}</span>
                      <motion.div
                        className="ml-4 p-1 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors"
                        whileHover={{ scale: 1.1 }}
                      >
                        <ChevronDown className="h-4 w-4 text-primary shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                      </motion.div>
                    </AccordionTrigger>
                    <AccordionContent className="relative text-muted-foreground pb-5">
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {faq.answer}
                      </motion.div>
                    </AccordionContent>
                  </AccordionItem>
                </div>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
